/**
 * Settings > Sweet Spot. Which store tablets get the new Help button. Cockpit
 * always shows it. A store in The Sweet Spot keeps the original help slide-out
 * and bug icon (Confluence articles, Jira tickets) until it is added here. Pick
 * a store from the list to add it, or remove one to send it back. Every change
 * saves at once and takes effect the next time that store opens The Sweet Spot.
 */

import { useState } from 'react';
import { FilterLabel } from '@/components/FilterLabel';
import { usePilotStores, usePutPilotStores, useStoresList } from '@/lib/useServiceDesk';
import { SdError } from './SdShell';
import { SD_BTN_GHOST, SD_BTN_PRIMARY, SD_CARD, SD_INPUT, SD_MUTED } from './sdStyles';

export function SweetSpotTab() {
  const pilot = usePilotStores();
  const stores = useStoresList();
  const put = usePutPilotStores();
  const [pick, setPick] = useState('');

  const list = pilot.data ?? [];
  const inList = new Set(list.map((s) => s.store_no));
  const options = (stores.data ?? []).filter((s) => !inList.has(s.store_no));
  const save = (next: string[]) => put.mutate(next);

  return (
    <section className={SD_CARD} aria-label="Sweet Spot pilot stores">
      <div className="flex max-w-2xl flex-col gap-3">
        <div>
          <FilterLabel as="div">Stores that get the new Help button in The Sweet Spot</FilterLabel>
          <p className={`text-[13px] ${SD_MUTED}`}>
            Every other store keeps the original help slide-out and the bug icon for reporting a problem. Cockpit
            always shows the new Help button, so nothing here changes it.
          </p>
        </div>
        {pilot.isLoading ? (
          <p className={`text-sm ${SD_MUTED}`}>Loading pilot stores...</p>
        ) : list.length === 0 ? (
          <p className={`text-[13px] ${SD_MUTED}`}>No stores yet. Every store has the original help.</p>
        ) : (
          <ul className="flex flex-col gap-1.5">
            {list.map((s) => (
              <li
                key={s.store_no}
                className="flex items-center justify-between gap-3 rounded-md border border-amp-line px-3 py-1.5 text-[13px] dark:border-amp-dark-line"
              >
                <span className="min-w-0">
                  <span className="text-amp-ink dark:text-amp-dark-ink">
                    {s.store_no} {s.name ?? ''}
                  </span>
                  {s.added_by && <span className={`ml-2 text-[12px] ${SD_MUTED}`}>added by {s.added_by}</span>}
                </span>
                <button
                  type="button"
                  className={SD_BTN_GHOST}
                  disabled={put.isPending}
                  onClick={() => save(list.filter((x) => x.store_no !== s.store_no).map((x) => x.store_no))}
                  aria-label={`Remove store ${s.store_no}`}
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        )}
        <div className="flex max-w-md items-end gap-2">
          <div className="flex-1">
            <FilterLabel htmlFor="sd-pilot-store">Add a store</FilterLabel>
            <select id="sd-pilot-store" value={pick} onChange={(e) => setPick(e.target.value)} className={SD_INPUT}>
              <option value="">Choose a store</option>
              {options.map((s) => (
                <option key={s.store_no} value={s.store_no}>
                  {s.store_no} {s.name}
                </option>
              ))}
            </select>
          </div>
          <button
            type="button"
            className={SD_BTN_PRIMARY}
            disabled={!pick || put.isPending}
            onClick={() => {
              save([...list.map((x) => x.store_no), pick]);
              setPick('');
            }}
          >
            Add
          </button>
        </div>
        <SdError error={pilot.error ?? stores.error ?? put.error} what="save the pilot stores" />
      </div>
    </section>
  );
}
